import type { ComponentProps, ReactNode } from 'react';
import React, { useCallback, useEffect, useRef } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import type { Nullable } from 'corgi-types';
import type { WidthProps } from 'styled-system';
import { width } from 'styled-system';

interface WidthElementProps extends WidthProps {
  overflowHidden?: boolean;
}

const UnstyledWidthElement = styled.div<WidthElementProps>`
  ${width};
  ${({ overflowHidden = false }) =>
    overflowHidden &&
    css`
      overflow: hidden;
    `}
`;

const WidthElement = (props: ComponentProps<typeof UnstyledWidthElement>) => (
  <UnstyledWidthElement width={1} {...props} />
);

interface GetElementWidthEnabledProps {
  onWidthChange: (width: Nullable<number>) => void;
  children?: ReactNode;
  overflowHidden?: boolean;
}

const GetElementWidthEnabled = ({
  onWidthChange,
  children,
  overflowHidden,
}: GetElementWidthProps) => {
  const elementRef = useRef(null);
  const onWidthChangeRef = useRef(onWidthChange);
  onWidthChangeRef.current = onWidthChange;

  useEffect(() => {
    const onResize = () => {
      if (elementRef.current) {
        onWidthChangeRef.current(elementRef.current.getBoundingClientRect().width);
      }
    };
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, []);

  const setElementRef = useCallback(element => {
    elementRef.current = element;
    onWidthChangeRef.current(element ? element.getBoundingClientRect().width : null);
  }, []);

  return (
    <WidthElement overflowHidden={overflowHidden} ref={setElementRef}>
      {children}
    </WidthElement>
  );
};

interface GetElementWidthProps extends GetElementWidthEnabledProps {
  disabled?: boolean;
}

const GetElementWidth = ({
  disabled,
  children,
  overflowHidden,
  onWidthChange,
}: GetElementWidthProps) =>
  disabled ? (
    <WidthElement overflowHidden={overflowHidden}>{children}</WidthElement>
  ) : (
    <GetElementWidthEnabled overflowHidden={overflowHidden} onWidthChange={onWidthChange}>
      {children}
    </GetElementWidthEnabled>
  );

export default GetElementWidth;
